import { z } from 'zod';
import { NpcCharacterRelation, NpcPlaceRelation, NpcReputation } from '../entity/types.js';
import { FeatureType, TerrainType } from '../hex/types.js';
// ---- Hex placement filters ----------------------------------------------------------
// Mirrors the shared HexFilterGroup shape: outer array = OR, inner arrays = AND.
// [[A, B], [C]] resolves to (A AND B) OR C. terrain/feature validate against the real
// game enums so authoring typos (e.g. "village") are caught at load / validate:data time.
export const AdventureHexFilterSchema = z.object({
    terrain: z.nativeEnum(TerrainType).optional(),
    feature: z.nativeEnum(FeatureType).optional(),
});
export const AdventurePlacementSchema = z.object({
    preferredHexFilters: z.array(z.array(AdventureHexFilterSchema)),
});
// ---- NPCs ---------------------------------------------------------------------------
// `target` is a symbolic NPC id (targetType 'npc', the default) or — when
// targetType is 'place' — a symbolic adventure location id. `kind` selects the
// matching relation enum; `note` carries optional human-readable flavor.
export const AdventureNpcRelationshipSchema = z.object({
    target: z.string(),
    targetType: z.enum(['npc', 'place']).default('npc'),
    kind: z.union([z.nativeEnum(NpcCharacterRelation), z.nativeEnum(NpcPlaceRelation)])
        .default(NpcCharacterRelation.ACQUAINTANCE),
    note: z.string().optional(),
});
export const AdventureNpcSchema = z.object({
    id: z.string(),
    archetype: z.string(),
    motivations: z.array(z.string()),
    reputation: z.nativeEnum(NpcReputation),
    relationships: z.array(AdventureNpcRelationshipSchema).default([]),
});
// ---- Items --------------------------------------------------------------------------
export const AdventureItemLocationSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('npc'), id: z.string() }),
    z.object({ type: z.literal('hex'), id: z.string() }), // id is a location symbolic id
    z.object({ type: z.literal('unknown') }),
]);
export const AdventureItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    icon: z.string(),
    location: AdventureItemLocationSchema,
});
// ---- Locations ----------------------------------------------------------------------
export const AdventureLocationSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    placement: AdventurePlacementSchema,
});
// ---- NPC injections -----------------------------------------------------------------
export const AdventureNpcInjectionSchema = z.object({
    memory: z.string().optional(),
    motivation: z.string().optional(),
});
// ---- Start location -----------------------------------------------------------------
export const AdventureStartLocationSchema = z.object({
    type: z.enum(['npc', 'item', 'location']),
    id: z.string(), // symbolic id of the referenced entity
    distance: z.object({ min: z.number().int(), max: z.number().int() }),
});
// ---- Adventure ----------------------------------------------------------------------
// ---- Summary DTO (client-facing selection list) -------------------------------------
export const AdventureSummaryDTOSchema = z.object({
    id: z.string(),
    title: z.string(),
    levels: z.object({ min: z.number(), max: z.number() }),
    intro: z.string(),
});
export const AdventureSchema = z.object({
    id: z.string(),
    title: z.string(),
    levels: z.object({ min: z.number().int(), max: z.number().int() }),
    narrative: z.object({
        intro: z.string(),
        context: z.string(),
    }),
    goals: z.array(z.unknown()).default([]), // parsed but not processed; reserved for future plan
    'start-location': AdventureStartLocationSchema,
    npcs: z.array(AdventureNpcSchema),
    items: z.array(AdventureItemSchema),
    locations: z.array(AdventureLocationSchema),
    npc_injections: z.array(AdventureNpcInjectionSchema).default([]),
});
